import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { Loader } from '../../../components/Loader/Loader';
import { fetchAllOrdersAsyncForUseEffect } from '../../../fetch/orderFetch';
import {
  Address,
  AddressValidity,
  OrderClazz,
  OrderSearchStatus,
  Region,
} from '../../../types/orderTypes';
import { defaultSortOrderForStatus, OrderSort } from '../Orders';
import './AddressFix.scss';
import SelectLocationContent from '../../../components/modals/SelectLocationContent';
import { onAddressChange, onAddressLocationChange } from './postNewAddress';
import Swal from 'sweetalert2';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';
import { Paper } from '@material-ui/core';

function sortOrders(orders: OrderClazz[], orderBy: OrderSort): OrderClazz[] {
  if (orderBy === OrderSort.Delivery) {
    const sortedOrdersByDelivery = orders.sort((a, b) => {
      const deliverComp = a.deliverFromDate.diff(b.deliverFromDate);
      return deliverComp !== 0
        ? deliverComp
        : a.createdDateDate.diff(b.createdDateDate);
    });
    return sortedOrdersByDelivery;
  } else if (orderBy === OrderSort.Created) {
    const sortedOrdersByCreated = orders.sort((a, b) =>
      a.createdDateDate.diff(b.createdDateDate)
    );
    return sortedOrdersByCreated;
  }
}

export function AddressFixViewShowCaroussel({
  problemOrders,
}: {
  problemOrders: OrderClazz[];
}): JSX.Element {
  const [showIndex, setShowIndex] = useState(0);

  const [showProblemOrders, setShowProblemOrders] =
    useState<OrderClazz[]>(problemOrders);

  function setUpdatedAdressTimesMethod() {
    Swal.fire({
      position: 'center',
      icon: 'success',
      title: 'The address was sucessfully updated',
      showConfirmButton: false,
      timer: 1500,
    }).then(() =>
      setShowProblemOrders(
        showProblemOrders.filter(
          (obj: OrderClazz, index: number) => index !== showIndex
        )
      )
    );
  }

  return (
    <>
      <div className="address-fix-modal">
        <Paper elevation={3}>
          <div className="address-fix-modal-header">
            <div className="position-buttonBack">
              <ButtonComponent
                disabled={showIndex === 0}
                variant="text"
                className="btn-arrow"
                onClick={() => setShowIndex(showIndex - 1)}
              >
                <IoIosArrowBack /> Back
              </ButtonComponent>
            </div>
            <span className="headerText">
              {`Order ${showIndex + 1} of ${
                showProblemOrders.length > 0 ? showProblemOrders.length : 0
              }`}
            </span>
            <div className="position-buttonNext">
              <ButtonComponent
                disabled={showIndex + 1 === showProblemOrders.length}
                variant="text"
                className="btn-arrow"
                onClick={() => setShowIndex(showIndex + 1)}
              >
                Next <IoIosArrowForward />
              </ButtonComponent>
            </div>
          </div>
          <div className="positionBox">
            <div className="bigBoxCenter">
              {showProblemOrders.length > 0 && (
                <React.Fragment>
                  <AddressFixView
                    key={`${showProblemOrders.length}`}
                    order={
                      showProblemOrders[showIndex] &&
                      showProblemOrders[showIndex]
                    }
                    setUpdatedAdressTimesMethod={setUpdatedAdressTimesMethod}
                  />
                </React.Fragment>
              )}{' '}
            </div>
          </div>
        </Paper>
      </div>
    </>
  );
}

function AddressFixView({
  order,
  setUpdatedAdressTimesMethod,
}: {
  order: OrderClazz;
  setUpdatedAdressTimesMethod: () => void;
}) {
  const [searchAddressFound, setSearchAddressFound] = useState<string | null>(
    null
  );
  const [updateSearchAddressResult, setUpdateSearchAddressResult] =
    useState<Address | null>(null);

  const [longitude, setLongitude] = useState<number>(order.recipientLongitude);
  const [latitude, setLatitude] = useState<number>(order.recipientLatitude);

  return (
    <>
      <SelectLocationContent
        longitude={order.recipientLongitude}
        latitude={order.recipientLatitude}
        targetAddress={order.recipientAddress}
        searchName={`${order.recipientAddress.substring(
          0,
          order.recipientAddress.indexOf(',')
        )}`}
        updateLongitude={(lng: number) => {
          setLongitude(lng);
        }}
        updateLatitude={(lat: number) => {
          setLatitude(lat);
        }}
        updateSearchAddressResult={(value: Address | null) => {
          setUpdateSearchAddressResult(value);
          console.log('updateSearchAddressResult', value);
        }}
        updateSearchAddressFound={(value: string | null) => {
          setSearchAddressFound(value);
          console.log('updateSearchAddressFound', value);
        }}
        recipientPhoneNumber={order.recipientPhoneNumber}
        orderReference={order.orderReference}
        recipientEmail={order.recipientEmail}
        recipientName={order.recipientName}
        orderId={order.id}
        date={order.deliverFrom}
        noteForCourier={order.noteForCourier}
      />

      <ButtonComponent
        onClick={() => {
          onAddressLocationChange(
            order.id,
            longitude,
            latitude,
            setUpdatedAdressTimesMethod
          );
        }}
        className="use-location-button"
      >
        Use location in map
      </ButtonComponent>

      <ButtonComponent
        disabled={!searchAddressFound}
        onClick={() => {
          onAddressChange(
            updateSearchAddressResult,
            order.id,
            setUpdatedAdressTimesMethod
          );
        }}
      >
        Replace with address found
      </ButtonComponent>
    </>
  );
}

export function AddressFix({ region }: { region: Region }): JSX.Element {
  const orderStatus = OrderSearchStatus.Active;
  const [fetchedOrders, setFetchedOrders] = useState<OrderClazz[]>();

  const sortOrdersBy: OrderSort = defaultSortOrderForStatus(orderStatus);

  const dateRange: { startDate: string; endDate: string } = {
    startDate: dayjs(dayjs().add(-7, 'day')).format('YYYY-MM-DD'),
    endDate: dayjs(dayjs().add(7, 'day')).format('YYYY-MM-DD'),
  };

  useEffect(() => {
    fetchAllOrdersAsyncForUseEffect(
      dateRange.startDate,
      dateRange.endDate,
      orderStatus,
      region
    ).then(orders => setFetchedOrders(orders));
  }, [dateRange.endDate, dateRange.startDate, orderStatus, region]);

  let problemOrders: OrderClazz[] = undefined;
  if (fetchedOrders) {
    const sortedOrders = sortOrders(fetchedOrders, sortOrdersBy);
    problemOrders = sortedOrders
      .filter(
        order =>
          order.recipientAddressValidity ===
          AddressValidity.NEEDS_MANUAL_VALIDATION
      )
      .sort((a, b) => dayjs(a.deliverFrom).diff(b.deliverFrom));
  }

  return (
    <div className="address-fix-background">
      <div className="address-fix-content">
        {!problemOrders && <Loader />}

        {problemOrders && (
          <>
            <AddressFixViewShowCaroussel
              key={`${problemOrders.length}`}
              problemOrders={problemOrders}
            />
          </>
        )}
      </div>
    </div>
  );
}
